import React, { createContext, useContext, useEffect, useMemo, useState } from 'react';
import { reportsApi } from '../lib/api';

const KEY = 'canales_report_filters';

const DEFAULT = {
  since: '',
  until: '',
  seller: '',
  category: [],
  region: [],
  brand: [],
};

const ReportFiltersContext = createContext(null);

export function ReportFiltersProvider({ children }) {
  const [filters, setFilters] = useState(() => {
    try {
      const raw = sessionStorage.getItem(KEY);
      if (raw) return { ...DEFAULT, ...JSON.parse(raw) };
    } catch { /* ignore */ }
    return DEFAULT;
  });

  const [options, setOptions] = useState({ sellers: [], categories: [], regions: [], brands: [], date_min: null, date_max: null });
  const [loadingOptions, setLoadingOptions] = useState(true);

  useEffect(() => {
    let cancelled = false;
    setLoadingOptions(true);
    reportsApi.filterOptions()
      .then((d) => { if (!cancelled) setOptions(d); })
      .catch(() => { /* keep defaults */ })
      .finally(() => { if (!cancelled) setLoadingOptions(false); });
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    try { sessionStorage.setItem(KEY, JSON.stringify(filters)); } catch { /* ignore */ }
  }, [filters]);

  const setFilter = (key, value) => setFilters((f) => ({ ...f, [key]: value }));

  const clear = () => setFilters(DEFAULT);

  // Build query params for axios — only include non-empty values
  const params = useMemo(() => {
    const p = {};
    if (filters.since) p.since = filters.since;
    if (filters.until) p.until = filters.until;
    if (filters.seller) p.seller = filters.seller;
    if (filters.category && filters.category.length) p.category = filters.category;
    if (filters.region && filters.region.length) p.region = filters.region;
    if (filters.brand && filters.brand.length) p.brand = filters.brand;
    return p;
  }, [filters]);

  const activeCount = useMemo(() => {
    let n = 0;
    if (filters.since || filters.until) n++;
    if (filters.seller) n++;
    n += (filters.category || []).length;
    n += (filters.region || []).length;
    n += (filters.brand || []).length;
    return n;
  }, [filters]);

  const value = { filters, setFilter, clear, params, options, loadingOptions, activeCount };
  return <ReportFiltersContext.Provider value={value}>{children}</ReportFiltersContext.Provider>;
}

export const useReportFilters = () => {
  const ctx = useContext(ReportFiltersContext);
  if (!ctx) throw new Error('useReportFilters must be used within ReportFiltersProvider');
  return ctx;
};
